import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from "react-router-dom";
import axios from "axios";
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess } from 'components/user-access';
import { HorizontalLinkGroup } from 'components/navigation';
import PropTypes from 'prop-types';
import { EventListing, EventSchedules, CostListing } from 'pages/asset/events';
import { AssetService, FleetService } from "services";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";

export function Events({
    assetId,
    asset,
    eventTypesForAsset,
    eventTypesChanged
}) {

    const [mappedAssetOptions, setMappedAssetOptions] = useState({});
    const [eventOptions, setEventOptions] = useState({});
    const [loading, setLoading] = useState(false);
    const [showCostsTab, setShowCostsTab] = useState(false);
    const requiredActions = ["FMS:View", "FMS:Edit", "FMS:Manage"];

    useEffect(() => {
        setLoading(true);
        setShowCostsTab(location.pathname.includes("/events/costs"));
        axios.all([
            AssetService.getAssetOptions(),
            FleetService.getEventOptions(),
            FleetService.getEventTypesForAccount()
        ]).then(axios.spread(function (assetOptionsResponse, eventOptionsResponse, eventTypesForAccountResponse) {
            if (assetOptionsResponse != null) {
                let ownershipAttributeMappings = assetOptionsResponse.assetOwnerships.map((x) => {
                    return { "name": x.name, "colours": x.colour, "id": x.id, "className": x.style, "icon": x.icon }
                });
                let assetStatusesMappings = assetOptionsResponse.assetStatuses.map((x) => {
                    return { "name": x.name, "colours": x.colour, "id": x.id }
                });

                setMappedAssetOptions(
                    { ownershipAttributeMappings: ownershipAttributeMappings, assetStatusesMappings: assetStatusesMappings, assetTypes: assetOptionsResponse.assetTypes }
                );
            }
            setEventOptions(eventOptionsResponse);
            setShowCostsTab((Array.isArray(eventTypesForAccountResponse) && eventTypesForAccountResponse.some(et => et.includeCosts)));
            setLoading(false);
        }), () => {
            NotificationToaster.show(Intent.DANGER, "Could not load the event data needed. Please refresh the page.");
            setLoading(false);
        });
    }, []);


    const routes = [
        { location: `/asset/${assetId}/events/list`, title: "Events", requiredAction: requiredActions },
        { location: `/asset/${assetId}/events/event-schedules`, title: "Schedules", requiredAction: requiredActions },
    ];
    if (showCostsTab) {
        routes.push({ location: `/asset/${assetId}/events/costs`, title: "Costs", requiredAction: requiredActions });
    }

    return (
        <UserAccess perform={requiredActions}
            yes={() => (
                <div className="row">

                    <h2>Events</h2>

                    <div className="spacer-bottom-small">
                        <HorizontalLinkGroup links={routes} exactRouteMatch={false}></HorizontalLinkGroup>
                    </div>

                    <Routes>
                        <Route path="list" element={<EventListing
                                asset={asset}
                                mappedAssetOptions={mappedAssetOptions}
                                eventOptions={eventOptions}
                                loading={loading}
                                eventTypes={eventTypesForAsset}
                                pageState="PROFILE"
                            />} />
                        <Route path="event-schedules" element={<EventSchedules
                                asset={asset}
                                eventTypesChanged={eventTypesChanged}
                                pageState="PROFILE"
                                mappedAssetOptions={mappedAssetOptions}
                            />} />
                        {showCostsTab && (
                            <Route path="costs" element={<CostListing
                                    eventOptions={eventOptions}
                                    eventTypes={eventTypesForAsset}
                                    locationList={mappedAssetOptions?.assetLocations}
                                    pageState="PROFILE"
                                    asset={asset}
                                    mappedAssetOptions={mappedAssetOptions}
                                />} />
                        )}
                        <Route path="" element={<Navigate to={`/asset/${assetId}/events/list`} replace />} />
                    </Routes>
                </div>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}

Events.propTypes = {
    assetId: PropTypes.string.isRequired,
    asset: PropTypes.object,
    eventTypesForAsset: PropTypes.array,
    eventTypesChanged: PropTypes.func
};